import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../../../services/auth-service';
import { UserListFilter, UserListItem } from '../../../../services/auth-service.model';
import { RoleService } from '../../../../services/role/role-service';
import { RoleVTO } from '../../../../pages/admin-dashboard/user-management/user-management.model';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import {
  buildPageNumbers,
  getEndItem,
  getStartItem,
  LoadState,
} from '../../../../shared/ui/shared-tables/pagination/pagination.utils';
import { Filter } from './filter/filter';
import { UserTableComponent } from './user-table-component/user-table-component';
import { UserAction } from './user-table-component/user-table-component.model';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../services/scope/scope-constants';

const DEFAULT_PAGE_SIZE = 25;

function defaultFilters(pageSize: number): UserListFilter {
  return {
    fullName: '',
    roleIds: [],
    statusId: null,
    createdFrom: '',
    createdTo: '',
    lastModifiedOnFrom: '',
    lastModifiedOnTo: '',
    orderBy: 'CREATION_DATE',
    orderDir: 'DESC',
    pageNum: 0,
    pageSize,
  };
}

@Component({
  selector: 'app-users-list',
  imports: [RouterLink, Filter, UserTableComponent, Pagination, HasScopeDirective],
  templateUrl: './users-list.html',
  styleUrl: './users-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersList implements OnInit {
  readonly SCOPES = SCOPES;
  private router = inject(Router);
  private authService = inject(AuthService);
  private roleService = inject(RoleService);
  private localeId = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);

  usersList = signal<UserListItem[]>([]);
  rolesList = signal<RoleVTO[]>([]);
  state = signal<LoadState>('loading');
  totalItems = signal(0);
  currentPage = signal(1);
  itemsPerPage = signal(DEFAULT_PAGE_SIZE);

  isArabic = computed(() => this.localeId.startsWith('ar'));

  filters = signal<UserListFilter>(defaultFilters(DEFAULT_PAGE_SIZE));
  appliedFilters = signal<UserListFilter>({ ...this.filters() });

  isFiltersApplied = computed(() => {
    const f = this.appliedFilters();
    return !!(
      f.fullName ||
      f.roleIds?.length ||
      f.statusId ||
      f.createdFrom ||
      f.createdTo ||
      f.lastModifiedOnFrom ||
      f.lastModifiedOnTo
    );
  });

  totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.itemsPerPage())));
  startItem = computed(() => getStartItem(this.currentPage(), this.itemsPerPage(), this.totalItems()));
  endItem = computed(() => getEndItem(this.currentPage(), this.itemsPerPage(), this.totalItems()));
  pageNumbers = computed(() => buildPageNumbers(this.currentPage(), this.totalPages()));

  ngOnInit() {
    const subscription = this.roleService.getAllRoles().subscribe((res) => {
      this.rolesList.set(res.list || []);
      this.loadUsers();
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  loadUsers() {
    this.state.set('loading');
    const subscription = this.authService.getAllUsersByFilters(this.appliedFilters()).subscribe({
      next: (res) => {
        const allRoles = this.rolesList();
        const mappedList = (res.list || []).map((user) => ({
          ...user,
          roles: user.roles.map((r: any) => {
            const matched = allRoles.find((role) => role.id === r.id);
            return matched ? matched : r;
          }),
        }));
        this.usersList.set(mappedList);
        this.totalItems.set(res.total || 0);
        this.state.set('success');
      },
      error: () => this.state.set('error'),
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onApplyFilters(filtersFromChild: UserListFilter): void {
    this.filters.set({ ...filtersFromChild, pageNum: 0, pageSize: this.itemsPerPage() });
    this.appliedFilters.set({ ...this.filters() });
    this.currentPage.set(1);
    this.loadUsers();
  }

  onResetFilters(): void {
    const resetValue = defaultFilters(this.itemsPerPage());
    this.filters.set(resetValue);
    this.appliedFilters.set(resetValue);
    this.currentPage.set(1);
    this.loadUsers();
  }

  onToggleSortOrder(): void {
    const newDir = this.filters().orderDir === 'ASC' ? 'DESC' : 'ASC';
    this.filters.update((prev) => ({ ...prev, orderDir: newDir }));
    this.appliedFilters.set({ ...this.filters() });
    this.loadUsers();
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages() || page === this.currentPage()) return;
    this.currentPage.set(page);
    this.filters.update((prev) => ({ ...prev, pageNum: page - 1 }));
    this.appliedFilters.set({ ...this.filters() });
    this.loadUsers();
  }

  onPageSizeChange(size: number) {
    this.itemsPerPage.set(size);
    this.currentPage.set(1);
    this.filters.update((prev) => ({ ...prev, pageNum: 0, pageSize: size }));
    this.appliedFilters.set({ ...this.filters() });
    this.loadUsers();
  }

  handleAction({ action, userId }: { action: UserAction; userId: number }): void {
    switch (action) {
      case 'View':
        this.router.navigate(['/admin/user-management/view', userId]);
        break;
      case 'Edit':
      case 'Deactivate':
      case 'Delete':
        break;
    }
  }
}
